import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Segmented } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { FileTextIcon, PlusIcon, SearchIcon } from '@/shared/ui/icons';
import { formatDate } from '@/shared/utils/format';

import { QuoteStatusBadge } from './QuoteStatusBadge';
import { money } from './format';
import { QUOTE_STATUS_LABELS, type Quote, type QuoteStatus } from './types';
import { useCreateQuote, useQuoteList } from './useQuotes';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short' });

const PAGE_SIZE = 50;

const STATUSES: { value: QuoteStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'draft', label: 'Borradores' },
  { value: 'reviewed', label: 'Revisados' },
  { value: 'sent', label: 'Enviados' },
  { value: 'cancelled', label: 'Anulados' },
];

const HELP = [
  <>
    <strong>Borrador</strong>: se puede tocar todo y no tiene número; lo recibe al aprobar.{' '}
    <strong>Revisado</strong>: aprobado, con número y PDF, ya no se edita. <strong>Enviado</strong>:
    alguien lo mandó al cliente. <strong>Anulado</strong>: descartado; el número, si lo tenía, se
    conserva.
  </>,
  <>
    Cuando llega una oferta de un proveedor por correo, el borrador aparece aquí solo, con sus
    líneas y costes. Ábrelo, elige cliente, ajusta márgenes y aprueba.
  </>,
  <>
    Para una oferta que no llegó por correo, <strong>Nuevo presupuesto</strong> crea uno vacío: se
    sube el documento o se escriben las líneas a mano.
  </>,
  <>
    La búsqueda mira número, proveedor y cliente, solo entre los de esta página. El estado filtra en
    el servidor.
  </>,
];

function emptyStateCopy({ search, status }: { search: string; status: QuoteStatus | '' }): {
  title: string;
  hint: string;
} {
  if (search.trim()) {
    return {
      title: 'Nada con esa búsqueda',
      hint: 'La búsqueda solo mira los presupuestos cargados en esta página.',
    };
  }
  if (status) {
    return {
      title: `Ningún presupuesto en ${QUOTE_STATUS_LABELS[status].toLowerCase()}`,
      hint: 'Prueba con otro estado, o con «Todos».',
    };
  }
  return {
    title: 'Todavía no hay presupuestos',
    hint: 'Llegan solos desde las ofertas de los proveedores. Para una que llegó por teléfono, «Nuevo presupuesto».',
  };
}

/**
 * Whose quote it is, for the listing.
 *
 * `client_name` is the snapshot frozen at approval, so an approved quote has it and a
 * draft does not, even when it points at a customer (`client_id`). Reading the live card
 * would be a request per row; the draft says it has one and the editor says who.
 */
function clientCell(quote: Quote): { text: string; quiet: boolean } {
  if (quote.client_name) return { text: quote.client_name, quiet: false };
  if (quote.client_id) return { text: 'Cliente elegido', quiet: true };
  return { text: '—', quiet: true };
}

/**
 * The status filter goes to the API, because it is paginated there. The text search does
 * **not**: it filters the page on screen, by number, supplier and the customer **of an
 * approved quote**. That is two deliberate limits and both are said on screen, because a
 * search that silently only looks at the current page, or that silently cannot see half
 * the rows, is worse than no search at all.
 *
 * A draft has no number: the column says «Borrador», in the quiet ink, and the status
 * badge is only painted when the row is not a draft, because the badge would repeat it.
 */
export function QuotesPage() {
  const [status, setStatus] = useState<QuoteStatus | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const quotes = useQuoteList({
    ...(status ? { status } : {}),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const visible = useMemo(() => filtered(quotes.data?.items ?? [], search), [quotes.data, search]);
  const navigate = useNavigate();
  const create = useCreateQuote();

  const newQuoteButton = (
    // An offer that came by phone, or a quote for something no supplier wrote down:
    // the draft is made empty and the editor does the rest.
    <Button
      icon={<PlusIcon size={18} />}
      onClick={() =>
        create.mutate(undefined, {
          onSuccess: (quote) => void navigate(`/presupuestos/${quote.id}`),
        })
      }
      disabled={create.isPending}
    >
      {create.isPending ? 'Creando…' : 'Nuevo presupuesto'}
    </Button>
  );

  return (
    <>
      <PageHeader title="Presupuestos" help={HELP} actions={newQuoteButton} />
      {create.isError ? (
        <ErrorMessage error={create.error}>No se pudo crear el presupuesto</ErrorMessage>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          label="Estado"
          value={status}
          options={STATUSES}
          onChange={(value) => {
            setStatus(value);
            setPage(0);
          }}
        />
        <div className="relative w-full sm:w-auto sm:min-w-[18rem] sm:flex-1 sm:max-w-sm">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Buscar"
            placeholder="Buscar por número, proveedor o cliente"
            className="field w-full pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {quotes.isPending ? <Spinner variant="block" label="Cargando presupuestos" /> : null}
      {quotes.isError ? (
        <ErrorMessage error={quotes.error}>No se pudo cargar la lista</ErrorMessage>
      ) : null}

      {quotes.data && visible.length === 0 ? (
        <EmptyState
          icon={<FileTextIcon size={24} />}
          {...emptyStateCopy({ search, status })}
          action={!search.trim() && !status ? newQuoteButton : undefined}
        />
      ) : null}

      {visible.length > 0 ? (
        <>
          <div className="hidden md:block">
            <Card padded={false}>
              <Table label="Presupuestos" minWidth="40rem">
                <THead>
                  <tr>
                    <Th>Número</Th>
                    <Th>Cliente</Th>
                    <Th>Proveedor</Th>
                    <Th>Fecha</Th>
                    <Th className="text-right">Total</Th>
                    <Th>Estado</Th>
                  </tr>
                </THead>
                <TBody>
                  {visible.map((quote) => {
                    const client = clientCell(quote);
                    return (
                      <Tr
                        key={quote.id}
                        interactive
                        onClick={() => void navigate(`/presupuestos/${quote.id}`)}
                      >
                        <Td>
                          {/* A real link inside the row, so the middle click and the context
                              menu work; it stops the row's own click so the same route is
                              not pushed twice. */}
                          <Link
                            to={`/presupuestos/${quote.id}`}
                            onClick={(event) => event.stopPropagation()}
                            className={
                              quote.quote_number
                                ? 'link font-mono text-[13.5px] font-semibold'
                                : 'text-ink-3 hover:text-ink font-medium'
                            }
                          >
                            {quote.quote_number ?? 'Borrador'}
                          </Link>
                        </Td>
                        <Td className={client.quiet ? 'text-ink-3' : 'text-ink'}>{client.text}</Td>
                        <Td className="text-ink-2">{quote.supplier_name ?? '—'}</Td>
                        <Td className="text-ink-2 whitespace-nowrap">
                          {formatDate(dateFormat, quote.created_at)}
                        </Td>
                        <Td className="num text-ink font-medium whitespace-nowrap">
                          {money(quote.total_gross)}
                        </Td>
                        <Td>
                          {quote.status !== 'draft' ? (
                            <QuoteStatusBadge status={quote.status} />
                          ) : null}
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </Card>
          </div>

          <CardList label="Presupuestos" className="md:hidden">
            {visible.map((quote) => {
              const client = clientCell(quote);
              return (
                <CardListItem
                  key={quote.id}
                  to={`/presupuestos/${quote.id}`}
                  title={
                    quote.quote_number ?? <span className="text-ink-3 font-medium">Borrador</span>
                  }
                  subtitle={[client.quiet ? null : client.text, quote.supplier_name]
                    .filter(Boolean)
                    .join(' · ')}
                  aside={
                    quote.status !== 'draft' ? <QuoteStatusBadge status={quote.status} /> : null
                  }
                  footer={
                    <>
                      <span>{formatDate(dateFormat, quote.created_at)}</span>
                      <span className="text-ink font-medium tabular-nums">
                        {money(quote.total_gross)}
                      </span>
                    </>
                  }
                />
              );
            })}
          </CardList>
        </>
      ) : null}

      {quotes.data && quotes.data.total > PAGE_SIZE ? (
        <div className="text-ink-3 flex items-center justify-end gap-3 text-[13px]">
          <span className="tabular-nums">
            {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + quotes.data.items.length} de{' '}
            {quotes.data.total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Anteriores
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={(page + 1) * PAGE_SIZE >= quotes.data.total}
            onClick={() => setPage((current) => current + 1)}
          >
            Siguientes
          </Button>
        </div>
      ) : null}
    </>
  );
}

function filtered(items: Quote[], search: string): Quote[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((quote) =>
    [quote.quote_number, quote.client_name, quote.supplier_name]
      .filter((field): field is string => Boolean(field))
      .some((field) => field.toLowerCase().includes(needle)),
  );
}
